import React, { MouseEventHandler, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';

import {
  TooltipContent,
  TooltipRoot,
  TooltipTrigger,
  IconButton,
  Button,
  Icon,
} from '@socialchorus/shared-ui-components';

import { trackContentCardShare } from '../../../models/content/analytics';
import { connect } from 'react-redux';
import { contentSelectors } from '../../../models/content';
import { ID as ContentCardShareModalID } from '../content-card/content-card-share-modal/content-card-share-modal';
import { uiOperations } from '../../../models/ui';
import {
  RootPatronState,
  usePatronSelector,
} from '../../../common/use-patron-selector';

interface IContentActionShare {
  isShared: boolean;
  onClick: MouseEventHandler | undefined;
  contentTitle: string;
  showText?: boolean;
  wideMode?: boolean;
  container?: HTMLElement | null;
}

const ContentActionShare: React.FC<IContentActionShare> = ({
  isShared,
  onClick,
  contentTitle,
  showText = false,
  wideMode = false,
  container,
}) => {
  const { t } = useTranslation(undefined, { useSuspense: false });

  //solar icon button's fill is not the secondary color, but solar text buttons do not respect this.
  //this will need discussion with design team, for now the shim here will make them work the same.
  const iconButtonClassName = cx('solar-shim', {
    'content__action--shared': isShared,
  });

  const buttonElement = useRef<HTMLButtonElement>(null);

  const displayText = showText || wideMode;

  const actionLabel = t('content.share_action', { title: contentTitle });

  return displayText ? (
    <Button
      variant="text"
      leftIcon={<Icon>share</Icon>}
      ref={buttonElement}
      onClick={onClick}
      aria-label={actionLabel}
      label={t('content.share')}
      selected={isShared}
    />
  ) : (
    <TooltipRoot>
      <TooltipTrigger asChild>
        <IconButton
          aria-label={actionLabel}
          iconName="share"
          onClick={onClick ? onClick : undefined}
          iconFilled={isShared}
          className={iconButtonClassName}
        />
      </TooltipTrigger>
      <TooltipContent description={t('content.share')} container={container} />
    </TooltipRoot>
  );
};

type ContentActionShareWrapperStateProps = ReturnType<typeof mapStateToProps>;
type ContentActionShareWrapperDispatchProps = typeof mapDispatchToProps;
type ContentActionShareWrapperOwnProps = {
  contentId: number;
  analyticsData: IAnalyticsData;
  showText?: boolean;
  wideMode?: boolean;
};

type ContentActionShareWrapperProps = ContentActionShareWrapperStateProps &
  ContentActionShareWrapperDispatchProps &
  ContentActionShareWrapperOwnProps;

const ContentActionShareWrapper = ({
  contentId,
  isShared,
  analyticsData,
  showText,
  addOverlay,
  wideMode,
  container,
}: ContentActionShareWrapperProps & { container?: HTMLElement | null }) => {
  const content = usePatronSelector((state) =>
    contentSelectors.getContentById(state, { contentId })
  );

  const handleClick: MouseEventHandler = (e) => {
    e.stopPropagation();

    addOverlay(ContentCardShareModalID, {
      shouldSnapToTop: false,
      contentId: content.id,
    });

    trackContentCardShare(content.id, analyticsData);
  };

  return (
    <ContentActionShare
      isShared={isShared}
      showText={showText}
      onClick={handleClick}
      contentTitle={content.title}
      wideMode={wideMode}
      container={container}
    />
  );
};

export { ContentActionShare };

const mapStateToProps = (
  state: RootPatronState,
  ownProps: ContentActionShareWrapperOwnProps
) => ({
  isShared: contentSelectors.getContentShared(state, {
    contentId: ownProps.contentId,
  }),
});

const mapDispatchToProps = {
  addOverlay: uiOperations.addOverlay,
  removeOverlay: uiOperations.removeOverlay,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ContentActionShareWrapper);
